import React, { useCallback } from 'react';

import { cn } from '@/lib/utils';
import { useAppState } from '@/state/hooks/app.state.hook';

interface CustomButtonProps {
  children: React.ReactNode;
  className?: string;
  onClick?: (e?: React.MouseEvent) => void;
  type?: 'button' | 'submit' | 'reset';
  disabled?: boolean;
  style?: React.CSSProperties;
  icon?: string | React.ReactNode;
  iconPosition?: 'left' | 'right';
  shouldUseStyle?: boolean;
}

export const CustomButton = ({
  children,
  onClick,
  className,
  style,
  type = 'button',
  disabled = false,
  icon,
  iconPosition = 'left',
  shouldUseStyle = true,
}: CustomButtonProps) => {
  const { design } = useAppState();

  const renderIcon = useCallback(() => {
    if (!icon) return null;

    if (typeof icon === 'string') {
      return <img src={icon} alt="button icon" className="size-5 object-contain" />;
    }

    return icon;
  }, [icon]);

  return (
    <button
      style={
        shouldUseStyle
          ? {
              borderRadius: '6px',
              border: `0px solid ${design!.buttonColor}`,
              background: `linear-gradient(to bottom, #EF9519, #FABF33)`,
              padding: '8px 16px',
              color: 'black',
              boxShadow: 'inset 0 1px 2px rgba(255,255,255,0.3)',
              ...style,
            }
          : {}
      }
      data-testid="custom-button"
      type={type}
      className={cn('flex items-center justify-center gap-2', className, disabled && 'opacity-50')}
      onClick={(e) => {
        e.stopPropagation();
        onClick?.(e);
      }}
      disabled={disabled}
    >
      {iconPosition === 'left' && renderIcon()}
      {children}
      {iconPosition === 'right' && renderIcon()}
    </button>
  );
};
